import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import BottomSlot from './BottomSlot';
import { audioEngine } from '../services/audioEngine';
import { replyTick } from '../services/uiSounds';
import { api } from '../services/api';
import { IS_GUEST } from '../config';

const IS_TOUCH = typeof window !== 'undefined'
  && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
import { useDialogueScene } from '../hooks/useDialogueScene';
import { useStoryAccess } from '../hooks/useStoryAccess';
import ChoiceChips from './ChoiceChips';
import VoiceMirror from './VoiceMirror';
import WalletTopUp from './WalletTopUp';

// SceneDock — the in-world surface for DIRECTED conversations (Stage One acts).
//
// Free-form chat with an artist lives in AvatarChatDock; this is the other kind:
// an NPC with a REASON to talk to you and an agenda of things to come away with.
// The player never sees a form — Book asks what you're making, what you sing,
// what to call it, and the answers become a real track.
//
// Deliberately thin: every rule (what gets asked, when it may end, whether a
// track may start) lives server-side in the dialogue director, and the turn loop
// itself lives in useDialogueScene — shared with the phone's call screen, so a
// conversation behaves identically whichever surface is painting it. This
// component owns the ROOM's version: who you're here as, ducking the district
// music, and the walk-in invitation.
//
// GATED by the server's story allowlist (useStoryAccess below — WORLD_STORY_USER_IDS),
// no longer by admin.
// `mark` names an object in the room to light up while the scene runs. It lives
// here, with the scene, because it is DIRECTION: the stylist says get in the
// chair, and the chair has to be the obvious chair. The NPC's words and the
// thing they refer to are one authored decision, so they belong in one place —
// splitting them is how a character ends up pointing at nothing.
const SCENE_BY_INTERIOR = {
  studio: { key: 'act3_session', invite: 'Start a session', quest: 'come_up_the_session' },
  // THE SALON CARRIES ITS QUEST (come_up_the_look). It never did — only the
  // admin StoryPanel passed one — so for a real player the look quest was never
  // started or synced, and everything chained behind it stayed locked: the
  // session quest (prerequisite: the look), Lyra's producer lineup, the walk's
  // studio waypoint, and so the drop and the handoff. The studio door still
  // opened (the story gate reads the profile, not quests), which is what hid it.
  salon: { key: 'act1_stylist', invite: 'See the stylist', mark: 'wash_chair_1', quest: 'come_up_the_look' },
};

const wrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  bottom: 'max(16px, env(safe-area-inset-bottom))', zIndex: 72,
  width: 'min(520px, calc(100vw - 32px))', maxHeight: 'min(60vh, 520px)',
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto',
  fontFamily: 'system-ui, sans-serif', color: '#ffe9c4',
  background: 'rgba(14,9,6,0.94)', border: '1px solid rgba(255,178,94,0.4)',
  borderRadius: 16, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const header = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(255,178,94,0.2)' };
const body = { padding: '12px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 };
const inputRow = { display: 'flex', gap: 8, padding: '10px 12px', borderTop: '1px solid rgba(255,178,94,0.2)' };
const inputStyle = { flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15, fontFamily: 'inherit', outline: 'none' };
const sendBtn = { padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', font: '700 14px system-ui', background: 'linear-gradient(180deg,#ffb25e,#ff5e78)', color: '#1a0f00' };
const iconBtn = { cursor: 'pointer', background: 'none', border: 'none', color: '#ffe9c4', fontSize: 20, lineHeight: 1, padding: 4 };
// Reads as a way out without competing with Send for the eye — outlined, not
// filled. See the header for why it stopped being a bare ×.
const leaveBtn = {
  cursor: 'pointer', padding: '6px 14px', borderRadius: 999,
  border: '1px solid rgba(255,178,94,0.45)', background: 'rgba(255,178,94,0.10)',
  color: '#ffe9c4', font: '700 12.5px system-ui', lineHeight: 1, whiteSpace: 'nowrap',
};
// NO `position: fixed` HERE ANY MORE. The invite lives in the bottom-centre
// stack (BottomSlot), which owns where the column sits; a surface that also
// pinned itself is exactly how it ended up on top of the live-chat chip.
const inviteBtn = {
  padding: '12px 22px', borderRadius: 999, border: '1px solid rgba(255,178,94,0.5)',
  background: 'rgba(20,12,8,0.9)', color: '#ffe9c4', font: '700 15px system-ui',
  cursor: 'pointer', pointerEvents: 'auto', backdropFilter: 'blur(8px)',
  whiteSpace: 'nowrap',
};

export default function SceneDock() {
  const interior = useWorldStore((s) => s.interior);
  const scene = useWorldStore((s) => s.scene);
  const openScene = useWorldStore((s) => s.openScene);
  const closeScene = useWorldStore((s) => s.closeScene);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  // A call is a scene too, and it renders on the phone. Whichever surface owns
  // the scene, only ONE may paint it — otherwise the same conversation appears
  // twice and each copy sends its own turns.
  const phoneCall = useWorldStore((s) => s.phoneCall);

  const [localNote, setLocalNote] = useState('');
  const [asName, setAsName] = useState('');   // WHOSE catalogue this fills
  const [mirror, setMirror] = useState(false);   // the optician (salon only)
  const endRef = useRef(null);

  // Server-driven story gate (WORLD_STORY_USER_IDS) — a fresh non-admin test
  // account is exactly who this must admit; the old isAdmin check made the
  // intro untestable by the account that matters most.
  const storyOpen = useStoryAccess();
  // The walk's choice carries into the room (§4.4): the studio runs the CHOSEN
  // producer's scene — same agenda, different person at the board. Falls back
  // to Book for anyone who never opened Lyra's text.
  const producer = useWorldStore((s) => s.producer);
  let available = SCENE_BY_INTERIOR[interior] || null;
  if (interior === 'studio' && producer && String(producer.avatarId) === String(myAvatarId) && producer.scene) {
    available = { ...available, key: producer.scene, invite: `Start a session with ${producer.name}` };
  }
  const mine = scene && !phoneCall;   // the phone is painting it, not us

  const conv = useDialogueScene({
    sceneKey: mine ? scene.key : null,
    quest: mine ? scene.quest : null,
    avatarId: myAvatarId,
  });
  const { messages, input, setInput, send, sendWorld, sayAuthored, sending, opening, choices, closed, note, pending, wallet, setWallet, lastRecorded } = conv;

  // ── THE LOOK RENDERS WHEN SASSY WRITES IT (Khari, 2026-08-03: "I don't
  // see any start of image generation") — the spec's fitting room: the
  // mirror artifact is a full-body pose, and pose mode reads the avatar's
  // stored appearance, which the salon just recorded. Fired once per visit;
  // the result lands IN the conversation as her own show-and-tell.
  // KHARI'S PIPELINE ORDER (2026-08-03, verbatim intent): "start the image
  // generation BEFORE the voice… Generate the image, mask it while it's
  // happening, then reveal it. When the user has agreed, immediately start
  // building the model and ask them to choose the voice." So: gen fires the
  // instant the look is defined; the reveal carries APPROVAL BUTTONS; the
  // yes makes the shot their FACE + queues the 3D BODY + arms the takes —
  // voice gets chosen while the slowest asset builds.
  const lookGenRef = useRef(false);
  const [lookOffer, setLookOffer] = useState(null);   // { images, idx, busy }
  const fireLookGen = useCallback((regenerate = false) => {
    if (lookGenRef.current || !myAvatarId) return;
    lookGenRef.current = true;
    setLocalNote('🎨 Your look is coming together — the mirror catches up in a moment.');
    api.funnelHeadshots(regenerate, myAvatarId)
      .then(({ res, data }) => {
        setLocalNote('');
        const images = res.ok ? (data?.images || []).filter((x) => x?.url) : [];
        if (images.length) {
          // The RESERVED MIRROR gets it too — the pane by the chair shows
          // the new you the moment it exists (and keeps showing you).
          useWorldStore.getState().setSalonLookUrl(images[0].url);
          setLookOffer({ images, idx: 0, busy: false });
          sayAuthored({
            content: images.length > 1
              ? "…and THERE it is. That's you — first of a few takes. That the one, or want to see the others?"
              : "…and THERE it is. That's you now — look's on your rack, mirror doesn't lie. That the one?",
            image: images[0].url,
          });
        } else {
          setLocalNote("The mirror didn't catch it this time — your look lands on your dashboard in a minute.");
        }
      })
      .catch(() => setLocalNote("The mirror didn't catch it this time — your look lands on your dashboard in a minute."));
  }, [myAvatarId, sayAuthored]); // eslint-disable-line react-hooks/exhaustive-deps

  // The AGREED moment — deterministic, world-owned, model can't skip it:
  // face is saved, the body starts building from it, the takes arm.
  const approveLook = useCallback(async () => {
    const img = lookOffer?.images?.[lookOffer.idx];
    if (!img?.url || lookOffer.busy) return;
    setLookOffer((o) => (o ? { ...o, busy: true } : o));
    try {
      // Promotes the chosen variant's BIBLE too — this face now drives
      // every future image, not just the profile.
      await api.funnelSelectHeadshot(img.id, myAvatarId);
      api.requestWorldEntry(myAvatarId)
        .then(({ res, data }) => {
          if (!res.ok) console.warn('[look] body build request refused:', data?.code || res.status);
          else console.info('[look] body build queued from the approved shot');
        })
        .catch(() => {});
      useWorldStore.getState().setSalonLookUrl(img.url);
      setMirror(true);
      sendWorld("(They approved the look — that picture is THEM now, and their body is being made from it. Offer the voice next: the takes play out loud right here.)");
      setLookOffer(null);
    } catch {
      setLookOffer((o) => (o ? { ...o, busy: false } : o));
      setLocalNote('Could not save the look — tap it again.');
    }
  }, [lookOffer, myAvatarId, sendWorld]);

  const otherLook = useCallback(() => {
    if (!lookOffer?.images || lookOffer.images.length < 2 || lookOffer.busy) return;
    const next = (lookOffer.idx + 1) % lookOffer.images.length;
    const img = lookOffer.images[next];
    useWorldStore.getState().setSalonLookUrl(img.url);
    sayAuthored({
      content: next === 0
        ? 'Back around to the first. Which one is you?'
        : `Or — this one. Same story, different person wearing it. (${next + 1} of ${lookOffer.images.length}.) Which is it?`,
      image: img.url,
    });
    setLookOffer((o) => (o ? { ...o, idx: next } : o));
  }, [lookOffer, sayAuthored]);

  // Trigger A — she records the look THIS visit. EVERY fresh write regens
  // (Khari, 2026-08-03: the first cut had no person in it and defaulted —
  // "I was never asked my ethnicity or gender"; correcting yourself to
  // Sassy re-records, and a re-record MUST make a new picture, not be
  // swallowed by the once-latch).
  useEffect(() => {
    if (!mine || scene?.key !== 'act1_stylist' || !myAvatarId) return;
    if ((lastRecorded || []).find((r) => r?.field === 'appearance')) {
      lookGenRef.current = false;
      setLookOffer(null);
      fireLookGen(true);   // a fresh write REPLACES the old set — new person, new takes
    }
  }, [mine, scene?.key, lastRecorded, myAvatarId, fireLookGen]);

  // Trigger B — the look was AUTHORED IN AN EARLIER VISIT but never
  // pictured (Khari, 2026-08-03: "where was the mirror? I never saw my
  // face… then she said I was complete" — his appearance predated the image
  // feature, so trigger A never fired again and the takes never armed).
  // Walk-in check: appearance set + no face on file → make the picture now;
  // appearance set + face exists → just arm the takes.
  useEffect(() => {
    if (!mine || scene?.key !== 'act1_stylist' || !myAvatarId || lookGenRef.current) return undefined;
    let dead = false;
    api.avatarDetail(myAvatarId)
      .then((d) => {
        if (dead || lookGenRef.current) return;
        const av = d?.avatar || d;
        if (!av?.appearance) return;              // not settled yet — trigger A's job
        if (av?.profile_image_url) {
          useWorldStore.getState().setSalonLookUrl(av.profile_image_url);
          setMirror(true);                        // face exists: takes only
        } else fireLookGen();                     // look authored, never pictured
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [mine, scene?.key, myAvatarId, fireLookGen]);

  // ── THE MIRROR PRESENTS ITSELF (Khari: "I don't know where any mirror
  // is") — when Sassy cues the mirror out loud in the salon, the mirror
  // OPENS; nobody hunts for a 16px icon they've never seen. Once per visit.
  // The voice takes arm DETERMINISTICALLY — no keyword sniffing ("mirror"
  // regex false-fired off Sassy's own vocabulary, and the mirror word now
  // means the LOOK anyway). The trigger lives in the look-gen effect below:
  // appearance recorded = look locked = the takes start warming, invisible
  // until their clips are playable, load masked by her talking.

  // ── The ride (Build_Game_Intro §4.2) ──────────────────────────────────────
  // RideSequence drives the car; this is where the car and the conversation
  // meet. HYBRID (Khari, 2026-08-02): a landmark going past speaks its
  // AUTHORED line — Spor1's cached lore, played the moment of passage, no
  // model in the loop — and only landmarks with no authored line fall back to
  // the parenthetical→LLM path. Everything else in the conversation stays live.
  const ride = useWorldStore((s) => s.ride);
  const rideNote = useWorldStore((s) => s.rideNote);
  const revealsRef = useRef(null);   // key → { line, audioUrl } once fetched
  useEffect(() => {
    if (!mine || !ride || revealsRef.current) return;
    api.rideReveals()
      .then((d) => {
        revealsRef.current = Object.fromEntries((d?.reveals || []).map((r) => [r.key, r]));
      })
      .catch(() => { revealsRef.current = {}; });   // live path carries the tour
  }, [mine, ride]);
  useEffect(() => {
    if (!mine || !ride || !rideNote) return;
    const authored = revealsRef.current?.[rideNote.key];
    // The authored lore is statements — the "get back to them" half of his
    // direction has to travel WITH it (Khari, 2026-08-03: three reveals
    // stacked and the ride went silent until he typed "ok"). The followUp is
    // a fixed sentence so a cluster of landmarks dedupes to ONE live nudge,
    // and Spor1 comes back with the next interview question unprompted.
    if (authored?.line) {
      sayAuthored({
        content: authored.line,
        audioUrl: authored.audioUrl,
        followUp: "(That's the city handled — get back to them now: pick the interview back up with your next question, one line.)",
      });
    } else sendWorld(`(The car is passing ${rideNote.name} — ${rideNote.line}.)`);
    useWorldStore.getState().setRideNote(null);
  }, [mine, ride, rideNote, sendWorld, sayAuthored]);

  // ── HIS VOICE IN THE CAR — same contract as the phone (PhoneCalls): every
  // assistant message may carry audioUrl (cached lore or a live line synthed
  // once). Each new spoken line replaces the last; leaving the scene cuts it.
  // The radio dips ONCE, when the first spoken line arrives, and stays dipped
  // for the scene — per-clip dips would pump the station with every sentence.
  const sceneAudioRef = useRef(null);
  const sceneSpokenRef = useRef(-1);
  const sceneDuckedRef = useRef(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!mine) { sceneSpokenRef.current = -1; return; }
    const idx = messages.length - 1;
    const m = messages[idx];
    if (!m || m.role !== 'assistant' || idx <= sceneSpokenRef.current) return;
    if (m.audioUrl) {
      sceneSpokenRef.current = idx;
      try {
        if (!sceneDuckedRef.current) { sceneDuckedRef.current = true; audioEngine.duck(true); }
        sceneAudioRef.current?.pause();
        const a = new Audio(m.audioUrl);
        sceneAudioRef.current = a;
        console.info('[voice] playing scene line', m.audioUrl.startsWith('data:') ? '(ephemeral)' : '(cached)');
        a.play().catch((e) => console.warn('[voice] play blocked:', e?.message));
      } catch (e) { console.warn('[voice] play failed:', e?.message); }
    } else if (!m.audioKey) {
      // TEXT-ONLY reply — no voice now, none coming (Khari, 2026-08-03: "we
      // are switching to text instead of voice, we need to notify the user…
      // a tick… that takes their focus to the chat input"). One soft tick per
      // reply, and on keyboard machines the input takes literal focus too.
      // audioKey pending means the voice is seconds away — no tick for those.
      sceneSpokenRef.current = idx;
      replyTick();
      if (!IS_TOUCH) inputRef.current?.focus();
    }
  }, [messages, mine]);
  useEffect(() => {
    if (mine) return undefined;
    sceneAudioRef.current?.pause();
    if (sceneDuckedRef.current) { sceneDuckedRef.current = false; audioEngine.duck(false); }
    return undefined;
  }, [mine]);
  useEffect(() => () => {
    sceneAudioRef.current?.pause();
    if (sceneDuckedRef.current) audioEngine.duck(false);
  }, []);

  // Bailing out of the conversation (the × button) is allowed — never trap —
  // but the CAR still has you. No scene + a moving ride = pull over now.
  useEffect(() => {
    if (ride && !scene) useWorldStore.getState().requestDropoff();
  }, [ride, scene]);

  // §5.1: only RIDING satisfies the ride quest — fields can arrive from the
  // website, the ride itself cannot (migration 279's objective). Reported when
  // Spor1's scene closes, with or without the car: the kerb is real however
  // the scene was run.
  useEffect(() => {
    if (!closed || !scene?.key?.startsWith('act2_ride_spor1')) return;
    conv.questCall('come_up_the_ride/progress', { objectiveType: 'goto', targetRef: 'ride_spor1' })
      .then(() => conv.questCall('come_up_the_ride/sync'))
      .catch(() => {});
  }, [closed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!closed || !ride) return undefined;
    useWorldStore.getState().requestDropoff();
    // The goodbye stays readable while the car pulls over, then the dock gets
    // out of the way — you're standing at the salon now, not in a chat window.
    const t = setTimeout(() => closeScene(), 4500);
    return () => clearTimeout(t);
  }, [closed, ride]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending, mirror]);

  // A session writes a real track into a real catalogue, and with several
  // artists it is genuinely easy to walk in as the wrong one — the world gives
  // no reminder of who you're currently embodying. Name them on the button and
  // in the header so it is decided before the session, not discovered after it.
  useEffect(() => {
    if (!myAvatarId) { setAsName(''); return; }
    let dead = false;
    api.myAvatars()
      .then((d) => {
        if (dead) return;
        const me = (d?.avatars || []).find((a) => String(a.avatar_id) === String(myAvatarId));
        setAsName(me?.name || '');
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [myAvatarId]);

  // Duck the district music while a scene runs — the room should be the room.
  useEffect(() => {
    if (!mine) return undefined;
    const wasOn = useWorldStore.getState().musicOn;
    if (wasOn) { audioEngine.setMusicOn(false).catch(() => {}); useWorldStore.setState({ musicOn: false }); }
    return () => { if (wasOn) { audioEngine.setMusicOn(true).catch(() => {}); useWorldStore.setState({ musicOn: true }); } };
  }, [mine, scene?.key]);

  // Leaving the room ends the scene — you can't be in a session you walked out
  // of. Rides and calls start OUTDOORS, so this only fires on an actual interior
  // change, never on a scene that was never in a room.
  useEffect(() => { if (scene?.room && !interior) { closeScene(); } }, [interior]); // eslint-disable-line react-hooks/exhaustive-deps

  // LIGHT UP WHAT SHE IS TALKING ABOUT, for as long as she is talking about it.
  //
  // This is the whole point of marks having been built as a general thing: the
  // scene says which object matters and the room makes it obvious, with neither
  // side knowing anything about the other. Sassy La's line "get in the chair"
  // and the chair glowing are the same authored decision arriving through two
  // channels.
  //
  // Cleared when the scene ends, because a mark is an instruction — one left
  // burning after the reason for it has gone is worse than none.
  useEffect(() => {
    if (!mine || !available?.mark) return undefined;
    useWorldStore.getState().setMarks([{ target: available.mark }]);
    return () => useWorldStore.getState().setMarks([]);
  }, [mine, scene?.key, available?.mark]);

  function begin(target) {
    // Guard against being handed something that isn't a scene spec (a DOM event,
    // most likely) rather than opening a scene with an undefined key.
    const spec = (target && typeof target === 'object' && typeof target.key === 'string') ? target : available;
    if (!spec?.key) { setLocalNote('No scene available here.'); return; }
    if (!myAvatarId) { setLocalNote('Pick which artist you\'re here as first.'); return; }
    setLocalNote('');
    // Opening the scene is all this does — the hook notices the new key, starts
    // the quest, and fetches the opening line.
    openScene({ key: spec.key, title: spec.invite, quest: spec.quest || null, room: interior || null });
  }

  if (!storyOpen) return null;

  // Not in a scene — offer one if this room has one. NOT to guests: their
  // scenes open from the phone and the kerb, and the salon shows them the
  // signup (GuestClaim) instead of a session invite.
  if (!mine) {
    if (IS_GUEST || scene || !available) return null;   // a phone call is running, or nothing here
    // The invite, its caption and any note are ONE surface — they read as a
    // unit and now lay out as one, in the bottom-centre stack. Rank 20 keeps
    // the studio's own call to action above the live-chat chip (rank 40).
    return (
      <BottomSlot order={20}>
        {localNote && <div style={{ ...inviteBtn, font: '600 13px system-ui', cursor: 'default' }}>{localNote}</div>}
        {/* NOT onClick={begin}: React would hand begin() the click event as its
            `target` argument, and a PointerEvent has no .key — so the scene
            opened with sceneKey undefined and the API refused it. */}
        <button style={inviteBtn} onClick={() => begin()}>
          🎙 {available.invite}{asName ? ` as ${asName}` : ''}
        </button>
        {asName && (
          <div style={{
            font: '600 11.5px system-ui', color: 'rgba(255,233,196,0.7)',
            pointerEvents: 'none', textAlign: 'center',
          }}>the track lands in {asName}&apos;s catalogue — switch artist above to change</div>
        )}
      </BottomSlot>
    );
  }

  const shown = note || localNote;

  return (
    <>
    <div style={wrap}>
      <div style={header}>
        <div style={{ font: '700 14px system-ui', letterSpacing: 0.3 }}>
          {scene.title}
          {asName && <span style={{ marginLeft: 8, font: '600 11.5px system-ui', opacity: 0.65 }}>as {asName}</span>}
          {pending?.running && <span style={{ marginLeft: 8, font: '600 12px system-ui', color: '#ffd27d' }}>● rendering</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* The optician (§4.3) — salon only. A change is OFFERED, never
              required; the gate itself is checked server-side when it opens. */}
          {scene.key === 'act1_stylist' && !closed && (
            <button style={{ ...iconBtn, fontSize: 16 }} onClick={() => setMirror(true)} title="Hear your takes — find your voice">🎙</button>
          )}
          {/* THE ESCAPE HAS TO LOOK LIKE ONE (Khari, 2026-08-06: "There is a
              'done' button that allows me to exit at any time.. We'd just need
              to make sure it is visible"). It matters more now than it did
              yesterday: the stylist can no longer declare the session finished
              early — the director refuses that until the face and the voice
              both exist — so this button is the ONLY way out of an unfinished
              session, and design law 2 rests entirely on it being findable.
              A bare × at 20px in a header was not.
              Labelled "Leave", not "Done", on purpose: leaving early is not
              finishing, and the word that means finishing now belongs to her
              line. */}
          <button style={leaveBtn} onClick={closeScene} aria-label="Leave">Leave</button>
        </div>
      </div>

      <div style={body}>
        {messages.map((m, i) => (
          // A world note is STAGE DIRECTION — an instruction to the NPC about
          // what the city just did, written in parentheses and addressed to
          // them, not to the player (Khari, 2026-08-03: "(That's the city
          // handled — get back to them now: pick the interview back up with
          // your next question, one line.)" was sitting in his transcript).
          // It still travels to the server, because it is what makes the NPC
          // react — it just never appears on screen. Same law as the NPC's own
          // parentheticals: the world speaks through what the character says.
          m.world ? null : (
            <div key={i} style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%', padding: '9px 12px', borderRadius: 12, fontSize: 14.5, lineHeight: 1.45,
              background: m.role === 'user' ? 'rgba(255,178,94,0.18)' : 'rgba(255,255,255,0.07)',
              border: m.role === 'user' ? '1px solid rgba(255,178,94,0.35)' : '1px solid rgba(255,255,255,0.09)',
            }}>
              {/* A message can carry a picture — the salon's look reveal.
                  Signed URL in an <img> is fine; only fetch() is CORS-walled. */}
              {m.image && (
                <img src={m.image} alt="" style={{
                  display: 'block', width: '100%', maxWidth: 240, borderRadius: 10,
                  margin: '0 auto 8px',
                }} />
              )}
              {m.display || m.content}
            </div>
          )
        ))}
        {sending && <div style={{ alignSelf: 'flex-start', opacity: 0.6, fontSize: 13 }}>…</div>}
        {!closed && <ChoiceChips choices={choices} onPick={send} disabled={sending} />}
        {shown && <div style={{ fontSize: 12.5, color: '#ffd27d', opacity: 0.9 }}>{shown}</div>}
        {/* The optician, IN the conversation (Khari, 2026-08-03: "those
            results should show in the chat log — no need for a new modal").
            In-flow it can't fight the room chrome for clicks like the old
            fixed panel did (the Done button was sitting on its buttons).
            Its commit drops a world note so Sassy REACTS to the voice
            settling. */}
        {mirror && (
          <VoiceMirror
            avatarId={myAvatarId}
            onDone={() => setMirror(false)}
            onNote={(t) => sendWorld(t)}
          />
        )}
        <div ref={endRef} />
      </div>

      {/* The top-up surface (Khari, 2026-08-03): a charging scene the wallet
          can't cover shows the way to pay RIGHT HERE — hosted checkout in a
          new tab, this panel watches the balance, and when the credits land
          the scene is told so Sassy can take the yes she already has. Shown
          even after a close: topping up and tapping her again is the path. */}
      {/* THE AGREED MOMENT — world-owned buttons under the revealed look.
          The yes here is what makes it real: face saved, body queued, takes
          armed. No conversation required, no way for the model to skip it. */}
      {lookOffer && (
        <div style={{ margin: '0 12px 8px', display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={approveLook}
            disabled={lookOffer.busy}
            style={{
              padding: '9px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
              font: '800 13px system-ui', background: 'linear-gradient(180deg,#ffb25e,#ff5e78)',
              color: '#1a0f00', opacity: lookOffer.busy ? 0.6 : 1,
            }}
          >{lookOffer.busy ? 'Saving…' : "That's me — lock it"}</button>
          {lookOffer.images.length > 1 && (
            <button
              onClick={otherLook}
              disabled={lookOffer.busy}
              style={{
                padding: '9px 14px', borderRadius: 999, cursor: 'pointer',
                border: '1px solid rgba(255,233,196,0.35)', background: 'none',
                color: 'rgba(255,233,196,0.85)', font: '700 12.5px system-ui',
              }}
            >Show me the other take</button>
          )}
        </div>
      )}

      {wallet?.short && (
        <WalletTopUp
          wallet={wallet}
          onCleared={() => {
            setWallet(null);
            if (!closed) sendWorld('(Their wallet just cleared — the treatment is covered now. Take the payment on their yes and get to work.)');
          }}
          onDismiss={() => setWallet(null)}
        />
      )}

      {!closed ? (
        <div style={inputRow}>
          <input
            ref={inputRef}
            style={inputStyle}
            value={input}
            placeholder="Say something…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            disabled={sending || opening}
          />
          <button style={sendBtn} onClick={() => send()} disabled={sending}>Send</button>
        </div>
      ) : (
        <div style={{ ...inputRow, justifyContent: 'center', opacity: 0.75, fontSize: 13 }}>Session over.</div>
      )}

    </div>

    </>
  );
}
